import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import type * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Compares 2 or more expressions and returns a similarity score between 0 and 1.
 *
 * __NOTE:__ For 2 expressions returns a single similarity score, for 3+
 * expressions returns an array of similarity scores from pairwise comparisons.
 *
 * @param expressions The expressions to compare
 * @example
 * {'similarity': ['cat', 'bat']}
 * // Returns: 0.14 (single similarity score)
 *
 * {'similarity': ['cat', 'bat', 'rat']}
 * // Returns: [0.14, 0.14, 0.14] (array of pairwise similarity scores)
 * ```
 */
export interface Similarity<out T = any> {
  /** {@link Similarity} */
  readonly [Key.Similarity]: T extends string ? readonly Expression<T>[] : never
}
export namespace Similarity {
  export const isSimilarity: TypeChecker<Similarity> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<Similarity>({
        [Key.Similarity]: CommonArgs.isListOfExpressions,
      }),
  ).usingAlias('Fn.Similarity')
  export const assertIsSimilarity: TypeAsserter<Similarity> =
    TypeAsserter.Factory.from(isSimilarity)

  /**
   * @see {@link fnNode.Node}
   */
  export class Node<T extends string = string> extends fnNode.Node<
    T,
    Key.Similarity,
    readonly expressionNode.Node[]
  > {
    /**
     * @inheritdoc
     */
    public readonly type = common.value.types.Intrinsic.Decimal

    constructor(args: Node<T>['arg']) {
      super(Key.Similarity, args)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Similarity>['expressionsInArgs'] = fn =>
    fn[Key.Similarity]
  // TODO: We could implement some optimization, for example, if we have value
  // nodes, that are all equal to each another, we should consider similarity to
  // be 1
  export const HELPER: FnHelper<Similarity> = {
    createNode: standardNodeCreator.usingExpressionList({
      emptyValue: 1,
      finalizeArgs: args => args,
      getArgExpressions: expressionsInArgs,
      handleValue: node => node,
      nodeClass: Node,
      setupStash: () => undefined,
      treatSingleArgAs: 'empty',
    }),
    expressionsInArgs,
    typeChecker: isSimilarity,
  }
}
